import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import { VisibilityIcon, VisibilityOffIcon, ContentCopyIcon } from 'icons/materialIcons';
import { validateEmail } from 'utils/validator';
import { generatePassword } from 'utils/passwordGenerator';
import { createTeamUser } from 'apis/adminApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import TeamPhoneInputField from 'team/shared/components/TeamPhoneInputField';
import loadGoogleMapsScript from 'utils/loadGoogleMapsScript';
import { useNavigate } from 'react-router-dom';

const CreateTeamUserForm = () => {
    const navigate = useNavigate();
    // Form state
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [teamName, setTeamName] = useState('');
    const [teamDescription, setTeamDescription] = useState('');
    const [teamPhoneNumber, setTeamPhoneNumber] = useState('');
    const [teamAddress, setTeamAddress] = useState('');
    const [teamAddressZipCode, setTeamAddressZipCode] = useState('');
    const [teamAddressUnit, setTeamAddressUnit] = useState('');
    const [addressPredictions, setAddressPredictions] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [addressCoordinates, setAddressCoordinates] = useState(null);
    const [passwordMode, setPasswordMode] = useState('auto'); // 'auto' or 'manual'
    const [autoGeneratedPassword, setAutoGeneratedPassword] = useState('');
    const [manualPassword, setManualPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
    const [maxTeamMembers, setMaxTeamMembers] = useState(10);

    // Error states
    const [emailError, setEmailError] = useState('');
    const [fullNameError, setFullNameError] = useState('');
    const [teamNameError, setTeamNameError] = useState('');
    const [teamPhoneNumberError, setTeamPhoneNumberError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [error, setError] = useState('');

    // UI states
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [createdUser, setCreatedUser] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const emailInputRef = useRef(null);
    const addressInputHandlerRef = useRef(null);

    // Generate password on mount
    useEffect(() => {
        setAutoGeneratedPassword(generatePassword());
    }, []);

    // Initialize Google Maps autocomplete for address
    useEffect(() => {
        const initializeAutocomplete = () => {
            const tryInitialize = () => {
                const input = document.getElementById('team-address-input');
                if (input && window.google && window.google.maps && window.google.maps.places) {
                    // Remove existing handler if it exists
                    if (addressInputHandlerRef.current) {
                        input.removeEventListener('input', addressInputHandlerRef.current);
                    }

                    const autocomplete = new window.google.maps.places.AutocompleteService();

                    const handleInput = (e) => {
                        const value = e.target.value;
                        if (value && value.trim().length > 0) {
                            autocomplete.getPlacePredictions(
                                {
                                    input: value,
                                    componentRestrictions: undefined
                                },
                                (predictions, status) => {
                                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                                        setAddressPredictions(predictions);
                                    } else {
                                        setAddressPredictions([]);
                                    }
                                }
                            );
                        } else {
                            setAddressPredictions([]);
                        }
                    };

                    input.addEventListener('input', handleInput);
                    addressInputHandlerRef.current = handleInput;
                    return true;
                }
                return false;
            };

            if (!tryInitialize()) {
                setTimeout(() => {
                    if (!tryInitialize()) {
                        setTimeout(tryInitialize, 500);
                    }
                }, 100);
            }
        };

        // Check if input exists and Google Maps is loaded
        const checkAndInitialize = () => {
            const input = document.getElementById('team-address-input');
            if (input) {
                if (window.google && window.google.maps && window.google.maps.places) {
                    initializeAutocomplete();
                } else {
                    loadGoogleMapsScript()
                        .then(() => {
                            initializeAutocomplete();
                        })
                        .catch(error => {
                            console.error('Failed to load Google Maps:', error);
                        });
                }
            }
        };

        // Try immediately
        checkAndInitialize();

        // Also try after a short delay to ensure DOM is ready
        const timeoutId = setTimeout(checkAndInitialize, 100);

        return () => {
            clearTimeout(timeoutId);
            const input = document.getElementById('team-address-input');
            if (input && addressInputHandlerRef.current) {
                input.removeEventListener('input', addressInputHandlerRef.current);
                addressInputHandlerRef.current = null;
            }
        };
    }, []);

    // Geocode address function
    const geocodeAddress = async (address) => {
        return new Promise((resolve, reject) => {
            if (!window.google) {
                reject(new Error('Google Maps not loaded'));
                return;
            }

            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    const coords = {
                        lat: location.lat(),
                        lng: location.lng()
                    };
                    resolve(coords);
                } else {
                    reject(new Error('Geocoding failed'));
                }
            });
        });
    };

    // Handle place selection from autocomplete
    const handlePlaceSelect = (prediction) => {
        const placesService = new window.google.maps.places.PlacesService(
            document.createElement('div')
        );

        placesService.getDetails(
            { placeId: prediction.place_id },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    setTeamAddress(place.formatted_address);
                    setSelectedPlace(place);
                    setAddressPredictions([]);

                    if (place.geometry && place.geometry.location) {
                        const coords = {
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng()
                        };
                        setAddressCoordinates(coords);
                    }

                    const zipComponent = place.address_components.find(
                        component => component.types.includes('postal_code')
                    );
                    if (zipComponent) {
                        setTeamAddressZipCode(zipComponent.long_name);
                    }
                }
            }
        );
    };

    // Handle address blur (for manual addresses)
    const handleAddressBlur = async () => {
        if (teamAddress && !selectedPlace) {
            try {
                const coords = await geocodeAddress(teamAddress);
                setAddressCoordinates(coords);
            } catch (error) {
                console.error('Failed to geocode address:', error);
            }
        }
    };

    // Focus email input on mount
    useEffect(() => {
        emailInputRef.current?.focus();
    }, []);

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleEmailChange = (event) => {
        const inputEmail = event.target.value;
        setEmail(inputEmail);
        setEmailError('');
        setError('');

        if (inputEmail && !validateEmail(inputEmail)) {
            setEmailError('Invalid email address!');
        }
    };

    const handleFullNameChange = (event) => {
        setFullName(event.target.value);
        setFullNameError('');
        setError('');
    };

    const handleTeamNameChange = (event) => {
        setTeamName(event.target.value);
        setTeamNameError('');
        setError('');
    };

    const handleTeamDescriptionChange = (event) => {
        setTeamDescription(event.target.value);
    };

    const handleTeamPhoneNumberChange = (value) => {
        setTeamPhoneNumber(value);
        setTeamPhoneNumberError('');
        setError('');
    };

    const handleTeamAddressChange = (event) => {
        const newValue = event.target.value;
        setTeamAddress(newValue);
        setError('');

        if (!newValue) {
            setAddressPredictions([]);
            setSelectedPlace(null);
            setTeamAddressZipCode('');
            setTeamAddressUnit('');
            setAddressCoordinates(null);
        }
    };

    const handlePasswordChange = (event) => {
        setManualPassword(event.target.value);
        setPasswordError('');
        setError('');
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleGenerateNewPassword = () => {
        setAutoGeneratedPassword(generatePassword());
        setError('');
    };

    const handleTypeManual = () => {
        setPasswordMode('manual');
        setManualPassword('');
        setPasswordError('');
    };

    const handleUseAutoGenerated = () => {
        setPasswordMode('auto');
        setManualPassword('');
        setPasswordError('');
    };

    const validateForm = () => {
        let isValid = true;

        // Email validation
        if (!email) {
            setEmailError('Email is required');
            isValid = false;
        } else if (!validateEmail(email)) {
            setEmailError('Invalid email address!');
            isValid = false;
        }

        // Full Name validation
        if (!fullName || fullName.trim() === '') {
            setFullNameError('Full name is required');
            isValid = false;
        }

        // Team Name validation
        if (!teamName || teamName.trim() === '') {
            setTeamNameError('Team name is required');
            isValid = false;
        }

        // Password validation
        const currentPassword = passwordMode === 'auto' ? autoGeneratedPassword : manualPassword;
        if (!currentPassword) {
            setPasswordError('Password is required');
            isValid = false;
        } else if (passwordMode === 'manual' && currentPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            isValid = false;
        }

        return isValid;
    };

    const handleCopyCredentials = () => {
        const credentials = `Email: ${createdUser.email}\nPassword: ${createdUser.password}`;
        navigator.clipboard.writeText(credentials).then(() => {
            setSnackbar({
                open: true,
                message: 'Credentials copied to clipboard!',
                severity: 'success'
            });
        });
    };

    const handleCreateAnother = () => {
        // Reset form
        setEmail('');
        setFullName('');
        setTeamName('');
        setTeamDescription('');
        setTeamPhoneNumber('');
        setTeamAddress('');
        setTeamAddressZipCode('');
        setTeamAddressUnit('');
        setAddressPredictions([]);
        setSelectedPlace(null);
        setAddressCoordinates(null);
        setPasswordMode('auto');
        setAutoGeneratedPassword(generatePassword());
        setManualPassword('');
        setSendWelcomeEmail(true);
        setSuccess(false);
        setCreatedUser(null);
        setError('');

        emailInputRef.current?.focus();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const currentPassword = passwordMode === 'auto' ? autoGeneratedPassword : manualPassword;

            // Build full address with unit if provided
            let fullAddress = teamAddress;
            if (fullAddress && teamAddressUnit) {
                fullAddress = `${fullAddress}${teamAddressUnit ? ` Unit ${teamAddressUnit}` : ''}`;
            }

            const response = await createTeamUser({
                email,
                password: currentPassword,
                fullName,
                teamName,
                teamDescription,
                teamPhoneNumber: teamPhoneNumber || undefined,
                teamAddress: fullAddress || undefined,
                sendWelcomeEmail,
                maxTeamMembers
            });

            if (response.success) {
                setSuccess(true);
                setCreatedUser({
                    email: response.credentials.email,
                    password: response.credentials.password,
                    fullName: response.user.fullName,
                    teamName: response.team.name
                });
                setSnackbar({
                    open: true,
                    message: response.message || 'Team user created successfully!',
                    severity: 'success'
                });
            } else {
                throw new Error(response.message || 'Failed to create team user');
            }
        } catch (error) {
            setError(error.message || 'Failed to create team user. Please try again.');
            setSnackbar({
                open: true,
                message: error.message || 'Failed to create team user',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !loading) {
            handleSubmit(event);
        }
    };

    // Success state
    if (success && createdUser) {
        return (
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 500,
                    margin: '0 auto',
                    padding: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: '#4caf50',
                        textAlign: 'center',
                        marginBottom: 2
                    }}
                >
                    ✅ Success!
                </Typography>

                <Typography variant="body1" sx={{ textAlign: 'center', marginBottom: 2 }}>
                    Team user created successfully
                </Typography>

                <Box
                    sx={{
                        backgroundColor: '#f5f5f5',
                        padding: 2,
                        borderRadius: 1,
                        marginBottom: 2
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: 1 }}>
                        User Details:
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                        • Email: {createdUser.email}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                        • Name: {createdUser.fullName}
                    </Typography>
                    <Typography variant="body2">
                        • Team: {createdUser.teamName}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        backgroundColor: '#fff3cd',
                        padding: 2,
                        borderRadius: 1,
                        border: '1px solid #ffc107',
                        marginBottom: 2
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: 1 }}>
                        Login Credentials:
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                        Email: {createdUser.email}
                    </Typography>
                    <Typography variant="body2">
                        Password: {createdUser.password}
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyCredentials}
                    sx={{
                        marginBottom: 2,
                        textTransform: 'none',
                        width: '100%'
                    }}
                >
                    Copy Credentials
                </Button>

                <Button
                    variant="contained"
                    onClick={handleCreateAnother}
                    sx={{
                        backgroundColor: 'blue',
                        textTransform: 'none',
                        width: '100%'
                    }}
                >
                    Create Another User
                </Button>

                <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                        textTransform: 'none',
                        width: '100%',
                        marginTop: 1
                    }}
                >
                    Back to Dashboard
                </Button>

                <NotificationSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    handleClose={handleSnackbarClose}
                />
            </Box>
        );
    }

    // Form state
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 500,
                margin: '0 auto',
                padding: 3
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 600,
                    marginBottom: 1,
                    textAlign: 'center'
                }}
            >
                Create Team User
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    marginBottom: 3,
                    textAlign: 'center'
                }}
            >
                Create a new user account with team plan. The user will receive login credentials.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Email Field */}
                <TextField
                    inputRef={emailInputRef}
                    label="Email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={handleKeyDown}
                    error={!!emailError}
                    helperText={emailError}
                    required
                    fullWidth
                    disabled={loading}
                />

                {/* Full Name Field */}
                <TextField
                    label="Full Name"
                    type="text"
                    value={fullName}
                    onChange={handleFullNameChange}
                    onKeyDown={handleKeyDown}
                    error={!!fullNameError}
                    helperText={fullNameError}
                    required
                    fullWidth
                    disabled={loading}
                />

                {/* Team Name Field */}
                <TextField
                    label="Team Name"
                    type="text"
                    value={teamName}
                    onChange={handleTeamNameChange}
                    onKeyDown={handleKeyDown}
                    error={!!teamNameError}
                    helperText={teamNameError}
                    required
                    fullWidth
                    disabled={loading}
                />

                {/* Team Description Field */}
                <TextField
                    label="Team Description (Optional)"
                    type="text"
                    value={teamDescription}
                    onChange={handleTeamDescriptionChange}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    multiline
                    rows={3}
                    disabled={loading}
                />

                {/* Max Team Members Field */}
                <TextField
                    label="Max Team Members"
                    type="number"
                    value={maxTeamMembers}
                    onChange={(e) => setMaxTeamMembers(parseInt(e.target.value) || 0)}
                    onKeyDown={handleKeyDown}
                    required
                    fullWidth
                    disabled={loading}
                    inputProps={{ min: 1 }}
                    helperText="Limit the total number of members this team can have"
                />

                {/* Password Field */}
                {passwordMode === 'auto' ? (
                    <Box>
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={autoGeneratedPassword}
                            required
                            fullWidth
                            disabled
                            error={!!passwordError}
                            helperText={passwordError}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, marginTop: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleGenerateNewPassword}
                                sx={{ textTransform: 'none', flex: 1 }}
                                disabled={loading}
                            >
                                Generate New
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                onClick={handleTypeManual}
                                sx={{ textTransform: 'none', flex: 1 }}
                                disabled={loading}
                            >
                                Type Manual
                            </Button>
                        </Box>
                        {passwordError && (
                            <Typography color="error" sx={{ fontSize: '0.75rem', marginTop: 0.5, marginLeft: 1.75 }}>
                                {passwordError}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={manualPassword}
                            onChange={handlePasswordChange}
                            onKeyDown={handleKeyDown}
                            error={!!passwordError}
                            helperText={passwordError || 'Password must be at least 8 characters'}
                            required
                            fullWidth
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleUseAutoGenerated}
                            sx={{ textTransform: 'none', marginTop: 1 }}
                            disabled={loading}
                        >
                            Use Auto-Generated Password
                        </Button>
                    </Box>
                )}

                {/* Company Phone Number Field */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Company Phone Number (Optional)
                    </Typography>
                    <Box
                        sx={{
                            '& .react-tel-input': {
                                width: '100%'
                            },
                            '& .react-tel-input .form-control': {
                                paddingLeft: '52px !important'
                            },
                            '& .react-tel-input .country-list': {
                                zIndex: 9999
                            }
                        }}
                    >
                        <TeamPhoneInputField
                            value={teamPhoneNumber}
                            onChange={handleTeamPhoneNumberChange}
                            error={teamPhoneNumberError}
                            country="sg"
                            placeholder="Enter company phone number"
                            disabled={loading}
                            containerStyle={{ marginBottom: '0px' }}
                            dropdownStyle={{ zIndex: 9999 }}
                        />
                    </Box>
                </Box>

                {/* Company Address Field */}
                <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Company Address (Optional)
                    </Typography>
                    <TextField
                        id="team-address-input"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={teamAddress}
                        onChange={handleTeamAddressChange}
                        onBlur={handleAddressBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Backspace' && teamAddress.length === 1) {
                                setTeamAddress('');
                                setAddressPredictions([]);
                                setSelectedPlace(null);
                                setTeamAddressZipCode('');
                                setTeamAddressUnit('');
                                setAddressCoordinates(null);
                            } else {
                                handleKeyDown(e);
                            }
                        }}
                        placeholder="Enter company address"
                        disabled={loading}
                        sx={{ mb: 1 }}
                    />

                    {addressPredictions.length > 0 && (
                        <Box
                            sx={{
                                mt: -1,
                                mb: 2,
                                maxHeight: 200,
                                overflow: 'auto',
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                boxShadow: 1,
                                border: '1px solid #ddd',
                                position: 'relative',
                                zIndex: 1000
                            }}
                        >
                            {addressPredictions.map((prediction) => (
                                <Box
                                    key={prediction.place_id}
                                    sx={{
                                        p: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                    onClick={() => handlePlaceSelect(prediction)}
                                >
                                    <Typography variant="body2">
                                        {prediction.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                ZIP (Optional)
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={teamAddressZipCode}
                                onChange={(e) => setTeamAddressZipCode(e.target.value)}
                                disabled={loading}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                UNIT (Optional)
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={teamAddressUnit}
                                onChange={(e) => setTeamAddressUnit(e.target.value)}
                                disabled={loading}
                            />
                        </Box>
                    </Box>

                    {teamAddress && (
                        <Box sx={{ mb: 2, height: 200, border: '1px solid #ddd', borderRadius: 1 }}>
                            {addressCoordinates ? (
                                <iframe
                                    title="location-map"
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    src={`https://www.google.com/maps/embed/v1/view?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&center=${addressCoordinates.lat},${addressCoordinates.lng}&zoom=15`}
                                    allowFullScreen
                                />
                            ) : (
                                <iframe
                                    title="location-map"
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(teamAddress)}`}
                                    allowFullScreen
                                />
                            )}
                        </Box>
                    )}
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendWelcomeEmail}
                            onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                            disabled={loading}
                        />
                    }
                    label="Send Welcome Email (Email credentials to buyer)"
                    sx={{ width: '100%', marginBottom: 2 }}
                />

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                        backgroundColor: 'blue',
                        borderRadius: '4px',
                        textTransform: 'none',
                        width: '100%'
                    }}
                    disabled={loading || !!emailError || !!fullNameError || !!teamNameError || !!teamPhoneNumberError || !!passwordError}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Team User'}
                </Button>

                {error && (
                    <Typography
                        color="error"
                        sx={{
                            marginTop: 2,
                            fontSize: '0.9rem',
                            textAlign: 'center'
                        }}
                    >
                        {error}
                    </Typography>
                )}

                <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                        marginTop: 2,
                        textTransform: 'none',
                        width: '100%'
                    }}
                    disabled={loading}
                >
                    Back to Dashboard
                </Button>
            </Box>

            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleSnackbarClose}
            />
        </Box>
    );
};

export default CreateTeamUserForm;